/* eslint-disable react/prop-types */
import { precisionFixed } from 'd3';
import dayjs from 'dayjs';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor, getPastDates, rgbaColor } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useMemo } from 'react';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

// Consistent chart colors - these will be used for both lines and legends
const CHART_COLORS = ['#5470C6', '#ffd000', '#ff4f4f', '#91cc75', '#c966ed'];
const MEAN_COLOR = '#c966ed'; // Special color for Mean/Average

const tooltipFormatter = (params, Series, precision, unit) => {
  // Get all the selected series from params
  const visibleSeries = params.map(p => p.seriesName);

  return `<div>
    <h6 class="fs--2 mb-0 fw-semi-bold">${params[0].axisValueLabel}</h6>
    <div class="align-items-center">
      ${params
        .map(param => {
          const { value, seriesName, color } = param;

          // Skip if value is null or empty
          if (value == null || value === '') {
            return '';
          }

          const formattedValue =
            typeof value === 'number' ? value.toFixed(precision) : 'N/A';

          if (formattedValue !== 'N/A') {
            return `
            <p class='mb-0 text-600 fs--1'>
              <span style='display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 5px;'></span>
              ${seriesName}: <span class='text-800 fw-semi-bold fs--1'>${formattedValue} <span class='text-500 fw-normal fs--1'>${unit}</span></span>
            </p>`;
          }
          return '';
        })
        .join('')}
    </div>
  </div>`;
};

const getOptions = (data, xAxis, Series, unit, decimalPoint) => {
  // Create a mapping of series to colors
  const getSeriesColor = (metric, index) => {
    if (metric === 'Mean' || metric === 'Average') {
      return MEAN_COLOR;
    }
    return CHART_COLORS[index % CHART_COLORS.length];
  };

  // Configure each series with appropriate colors and styles
  const seriesConfig = Series.map((metric, index) => {
    const color = getSeriesColor(metric, index);
    const isMeanOrAvg = metric === 'Mean' || metric === 'Average';

    return {
      name: metric,
      type: 'line',
      data: data[index],
      showSymbol: 5,
      symbol: 'circle',
      itemStyle: {
        color: color,
        borderColor: color
      },
      lineStyle: {
        color: color,
        width: 1.5,
        type: 'solid'
      },
      // Higher z-index for Mean/Average series so it appears on top
      z: isMeanOrAvg ? 2 : 1
    };
  });

  return {
    // Set the colors for all series
    color: Series.map((metric, index) => getSeriesColor(metric, index)),
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      formatter: function (params) {
        return tooltipFormatter(params, Series, decimalPoint, unit);
      }
    },
    legend: {
      data: Series,
      textStyle: {
        color: getColor('gray-700')
      },
      left: 10,
      top: 0,
      icon: 'circle',
      inactiveColor: getColor('gray-400'),
      selectedMode: 'multiple'
    },
    xAxis: {
      type: 'category',
      data: xAxis,
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: getColor('gray-300')
        }
      },
      axisTick: { show: true },
      axisLabel: {
        color: '#87848A',
        margin: 5,
        showMaxLabel: true
      },
      splitLine: {
        show: false
      },
      axisPointer: {
        lineStyle: {
          color: getColor('gray-300')
        }
      }
    },
    yAxis: {
      position: 'right',
      name: unit,
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        lineStyle: {
          color: getColor('gray-200')
        }
      },
      axisLine: { show: false },
      axisLabel: { color: '#87848A' }
    },
    series: seriesConfig,
    grid: {
      containLabel: true,
      right: '10px',
      left: '15px',
      bottom: '0px',
      top: '10%'
    }
  };
};

const LineChartBody = ({
  chartdata,
  label,
  onLegendSelect,
  chartRef: externalChartRef
}) => {
  const { data, xAxis, Series, unit } = chartdata;
  const internalChartRef = useRef(null);
  const chartRef = externalChartRef || internalChartRef;

  // Determine decimal point precision based on label
  const decimalPoint = useMemo(() => {
    if (
      label === 'Nitrogen Dioxide' ||
      label === 'Carbon Monoxide' ||
      label === 'Sulphur Dioxide' ||
      label === 'Ozone'
    ) {
      return 6;
    }
    return 2;
  }, [label]);

  // Set up chart event listeners
  const setupChartEvents = chart => {
    // Clear previous event handlers to avoid duplicates
    chart.off('legendselectchanged');

    // Add legend selection handler
    chart.on('legendselectchanged', params => {
      if (onLegendSelect) {
        // Always pass the name of the clicked legend
        onLegendSelect(params.name);
      }
    });
  };

  // Initialize chart and set up event handlers
  useEffect(() => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      setupChartEvents(chartInstance);

      // Initially select the last series
      if (Series && Series.length > 0 && onLegendSelect) {
        onLegendSelect(Series[Series.length - 1]);
      }
    }

    // Clean up event handlers when component unmounts
    return () => {
      if (chartRef.current) {
        const chartInstance = chartRef.current.getEchartsInstance();
        chartInstance.off('legendselectchanged');
      }
    };
  }, [Series, onLegendSelect]);

  // Handle edge cases for empty data
  if (!data || !xAxis || !Series || Series.length === 0) {
    return <div className="text-center py-5">No chart data available</div>;
  }

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={getOptions(data, xAxis, Series, unit, decimalPoint)}
      onChartReady={setupChartEvents}
      style={{ height: '23.25rem' }}
      // Set notMerge to false to allow smoother legend toggling
      notMerge={false}
    />
  );
};

LineChartBody.propTypes = {
  chartdata: PropTypes.shape({
    data: PropTypes.array,
    xAxis: PropTypes.array,
    Series: PropTypes.array,
    unit: PropTypes.string
  }).isRequired,
  label: PropTypes.string,
  onLegendSelect: PropTypes.func,
  chartRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ])
};

export default LineChartBody;
